// PRO+ hub editor: the band (or THE WALL) changes the hub's words, videos, press, booking and links in one sheet.
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { API } from "../../App";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { Btn, INPUT, Eyebrow } from "../voltbox/ui";
import { errMsg } from "../voltbox/vbShared";
import { NEON, hubAuth } from "./hubApi";

const LINKS = [["instagram", "Instagram"], ["facebook", "Facebook"], ["bandcamp", "Bandcamp"], ["youtube", "YouTube"], ["discogs", "Discogs"]];

const init = (b) => {
  const h = b.hub || {};
  return {
    tagline: b.tagline || "", bio: b.bio || "", ticker: (h.ticker || []).join("\n"),
    videos: h.videos || [], press: h.press || [], booking_note: h.booking_note || "", booking_email: h.booking_email || "",
    ...Object.fromEntries(LINKS.map(([k]) => [k, b[k] || ""])),
  };
};

const Field = ({ label, hint, children }) => (
  <label className="block mb-5">
    <span className="block text-xs font-black uppercase tracking-[0.18em] text-white/70 mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-white/40 mt-1">{hint}</span>}
  </label>
);

const Rows = ({ label, rows, fields, max, onChange, testid }) => (
  <div className="mb-6" data-testid={testid}>
    <span className="block text-xs font-black uppercase tracking-[0.18em] text-white/70 mb-2">{label}</span>
    {rows.map((r, i) => (
      <div key={i} className="relative rounded-xl border border-white/10 p-3 pr-10 mb-2 space-y-2">
        {fields.map(([k, ph, long]) => {
          const Tag = long ? "textarea" : "input";
          return <Tag key={k} rows={long ? 3 : undefined} value={r[k] || ""} placeholder={ph} className={INPUT} onChange={(e) => onChange(rows.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} data-testid={`${testid}-${i}-${k}`} />;
        })}
        <button type="button" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="absolute top-3 right-2.5 text-white/40 hover:text-white" aria-label="Remove" data-testid={`${testid}-${i}-remove`}><X className="h-4 w-4" /></button>
      </div>
    ))}
    {rows.length < max && <button type="button" onClick={() => onChange([...rows, {}])} className="inline-flex items-center gap-1.5 text-xs font-bold text-[#54E3FF] hover:underline" data-testid={`${testid}-add`}><Plus className="h-3.5 w-3.5" /> Add</button>}
  </div>
);

export const HubEditSheet = ({ band, open, onOpenChange, onSaved }) => {
  const [f, setF] = useState(() => init(band));
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    const body = {
      ...f, ticker: f.ticker.split("\n").map((t) => t.trim()).filter(Boolean),
      videos: f.videos.filter((v) => (v.url || "").trim()).map((v) => ({ url: v.url.trim(), title: v.title || "" })),
      press: f.press.filter((p) => (p.source || "").trim()).map((p) => ({ quote: p.quote || "", source: p.source.trim(), url: (p.url || "").trim() })),
    };
    try { await axios.put(`${API}/bands/${band.slug}/hub`, body, hubAuth()); toast.success("Saved — it's live on the page"); onSaved(); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e, "Couldn't save")); }
    setBusy(false);
  };
  return (
    <Sheet open={open} onOpenChange={(o) => { if (o) setF(init(band)); onOpenChange(o); }}>
      <SheetContent className="bg-[#05090D] text-white border-l border-[#54E3FF]/30 w-full sm:max-w-xl overflow-y-auto" data-testid="hub-edit-sheet">
        <SheetHeader className="mb-6">
          <Eyebrow color={NEON}>PRO+ band hub</Eyebrow>
          <SheetTitle className="text-white font-['Playfair_Display'] text-2xl">Edit {band.name}&rsquo;s page</SheetTitle>
          <SheetDescription className="text-white/55">Changes go live as soon as you save.</SheetDescription>
        </SheetHeader>
        <Field label="Tagline"><input value={f.tagline} onChange={set("tagline")} maxLength={160} className={INPUT} data-testid="hub-edit-tagline" /></Field>
        <Field label="About the band"><textarea value={f.bio} onChange={set("bio")} rows={6} maxLength={3000} className={INPUT} data-testid="hub-edit-bio" /></Field>
        <Field label="News ticker" hint="One line each (up to 140 characters), up to 8 lines. They scroll across the top of the page."><textarea value={f.ticker} onChange={set("ticker")} rows={4} className={INPUT} data-testid="hub-edit-ticker" /></Field>
        <Rows label="Videos (YouTube or Vimeo)" rows={f.videos} max={8} onChange={(v) => setF({ ...f, videos: v })} fields={[["url", "https://www.youtube.com/watch?v=…"], ["title", "Title"]]} testid="hub-edit-videos" />
        <Rows label="Press" rows={f.press} max={8} onChange={(v) => setF({ ...f, press: v })} fields={[["quote", "Quote (optional)", true], ["source", "Who said it · where · when"], ["url", "Link to the article (https://…)"]]} testid="hub-edit-press" />
        <Field label="For venues & bookers"><textarea value={f.booking_note} onChange={set("booking_note")} rows={3} maxLength={600} className={INPUT} data-testid="hub-edit-booking-note" /></Field>
        <Field label="Booking email" hint="Optional. Leave empty to send bookers to Instagram and Facebook."><input type="email" value={f.booking_email} onChange={set("booking_email")} className={INPUT} data-testid="hub-edit-booking-email" /></Field>
        {LINKS.map(([k, label]) => <Field key={k} label={label}><input value={f[k]} onChange={set(k)} placeholder="https://…" className={INPUT} data-testid={`hub-edit-${k}`} /></Field>)}
        <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-[#05090D]/95 backdrop-blur border-t border-white/10 flex justify-end gap-2">
          <Btn variant="ghost" onClick={() => onOpenChange(false)} data-testid="hub-edit-cancel">Cancel</Btn>
          <Btn variant="cyan" onClick={save} disabled={busy} data-testid="hub-edit-save">{busy ? "Saving…" : "Save"}</Btn>
        </div>
      </SheetContent>
    </Sheet>
  );
};
